import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  AuthResponse,
  RespuestaDeIngreso,
  LoginCredentials,
  RegisterData,
  User,
  ForgotPasswordData,
  ResetPasswordData,
  Portal,
} from '../types/auth.types';

/**
 * Servicio de autenticación
 */
class AuthService {
  /**
   * Ingreso: valida la contraseña y dice a qué empresas puede entrar.
   *
   * NO abre sesión — eso lo hace `elegirPortal`.
   */
  async login(credentials: LoginCredentials): Promise<RespuestaDeIngreso> {
    return apiClient.post<RespuestaDeIngreso>(ENDPOINTS.AUTH_LOGIN, credentials);
  }

  /**
   * Registro de nueva empresa y usuario admin
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>(ENDPOINTS.AUTH_REGISTER, data);
  }

  /**
   * Refresh del token
   */
  async refreshToken(refreshToken: string): Promise<{ token: string }> {
    return apiClient.post<{ token: string }>(ENDPOINTS.AUTH_REFRESH, {
      refreshToken,
    });
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    return apiClient.post<void>(ENDPOINTS.AUTH_LOGOUT);
  }

  /**
   * Obtener usuario actual
   */
  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>(ENDPOINTS.AUTH_ME);
  }

  /**
   * Solicitar reset de contraseña
   */
  async forgotPassword(data: ForgotPasswordData): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(ENDPOINTS.AUTH_FORGOT_PASSWORD, data);
  }

  /**
   * Reset de contraseña
   */
  async resetPassword(data: ResetPasswordData): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(ENDPOINTS.AUTH_RESET_PASSWORD, data);
  }

  /**
   * Marcar onboarding como completado en el servidor
   */
  async completeOnboarding(): Promise<void> {
    return apiClient.patch<void>(ENDPOINTS.AUTH_ONBOARDING, {});
  }

  /**
   * Validar un link de invitación (público). Devuelve contexto para la página.
   */
  async validateInvitation(
    token: string
  ): Promise<{ companyName: string; allowedDomain: string; roleName: string }> {
    return apiClient.get(ENDPOINTS.PUBLIC_INVITATION(token));
  }

  /**
   * Registrarse vía link de invitación (público). Devuelve sesión (auto-login).
   */
  async acceptInvitation(
    token: string,
    data: { firstName: string; lastName: string; email: string; password: string }
  ): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>(ENDPOINTS.PUBLIC_INVITATION_ACCEPT(token), data);
  }

  /**
   * Canjea por una sesión en esa empresa.
   *
   * Se le pasa el token de selección explícitamente en vez de dejar que lo
   * tome el interceptor: el interceptor manda el token de sesión, y justo
   * después de un ingreso hace falta el de selección, que es otro.
   *
   * Si no se pasa `portalToken`, viaja el token de sesión que pone el
   * interceptor: es el camino después de recargar la página, cuando el token
   * de selección ya venció (dura 10 minutos) — `POST /auth/portal/:slug`
   * también lo acepta.
   */
  async elegirPortal(slug: string, portalToken?: string): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>(
      ENDPOINTS.AUTH_PORTAL_EXCHANGE(slug),
      {},
      portalToken ? { headers: { Authorization: `Bearer ${portalToken}` } } : undefined
    );
  }

  /** Los portales de quien está conectado. Sobrevive a un F5. */
  async misPortales(): Promise<Portal[]> {
    return apiClient.get<Portal[]>(ENDPOINTS.AUTH_PORTALS);
  }
}

export const authService = new AuthService();
export default authService;
